// (C) Copyright IBM Deutschland GmbH 2021.  All rights reserved.

/***********************************************************************************************
 imports
 ***********************************************************************************************/

import React from 'react';
import {Dimensions, Platform, StyleSheet} from 'react-native';
import PropTypes from 'prop-types';
import {navigationPropType, webViewPropType} from '../../propTypes';

// components
import {ListItem} from '@rneui/themed';

// config
import {appConfig, theme} from '../../config';
import {isMobile} from "react-device-detect";
import {useSelector} from "react-redux";
import {isUrl} from "../../services/utils";

/***********************************************************************************************
 * component:
 * Renders a ListItem which will navigate to another screen when clicked on
 *
 * @param  {object}    props
 * @param  {object} props.navigation the navigation object provided by 'react-navigation'
 * @param  {{
 *            title:string,
 *            subTitle: string,
 *      iconType: string,
 *      iconTitle: string,
 *            uri: string
 * }}    props.webView holds the strings and the link to open in the webView
 ***********************************************************************************************/
function AboutListLink({navigation, webView}) {
    let { width, height } = Dimensions.get('window');

    const {fontScaleFactor} = useSelector(
        (state) => state.Globals
    );

    let iconSize =
        appConfig.scaleUiFkt(Platform.OS === 'web' ? ( isMobile ? 18 : 12) : 12);
    iconSize *= width > height ? 0.5 : 1;
    return (
        <>
            {!webView.title && <ListItem></ListItem>}
            {!!webView.title &&
            <ListItem
                containerStyle={localStyle.containerStyle}
                onPress={() => {
                    // webView.uri can either contain a route or URL
                    if(isUrl(webView.uri)){
                        window.open(webView.uri);
                    } else {
                        navigation.navigate(webView.uri);
                    }
                }}
            >
                {/* title & subtitle of the listItem - the strings a identified by the webView*/}
                <ListItem.Content>
                    <ListItem.Title  style={{...localStyle.title, fontSize: localStyle.title.fontSize * fontScaleFactor }} testID="ALL_title">
                        {webView.title}
                    </ListItem.Title>

                    {webView.subTitle && (
                        <ListItem.Subtitle  style={{...localStyle.subTitle, fontSize: localStyle.subTitle.fontSize * fontScaleFactor }} testID="ALL_subTitle">
                            {webView.subTitle}
                        </ListItem.Subtitle>
                    )}
                </ListItem.Content>

                {/* the icon on the right-hand-side */}
                <ListItem.Chevron
                    {...{
                        type: webView.iconType,
                        name: webView.iconTitle,
                        color: theme.values.defaultListLinkIconColor,
                        reverse: true,
                        size: iconSize,
                    }}
                />
            </ListItem>}
        </>
    );
}

AboutListLink.propTypes = {
    navigation: PropTypes.shape(navigationPropType).isRequired,
    webView: PropTypes.shape(webViewPropType).isRequired,
};

/***********************************************************************************************
 local styling
 ***********************************************************************************************/

const localStyle = StyleSheet.create({
    containerStyle: {
        width: '100%',
        borderBottomColor: theme.colors.accent3,
        borderBottomWidth: 1,
        backgroundColor: theme.values.defaultListLinkBackgroundColor,
        padding: 15,
    },

    subTitle: {
        color: theme.colors.accent4,
        ...theme.fonts.body,
    },

    title: {
        ...theme.fonts.title2,
    },
});

/***********************************************************************************************
 export
 ***********************************************************************************************/

export default AboutListLink;
